import { SeloDaCampanha } from '../cptu/CampanhaMoldura';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  acaoDoRelatorioSpt, anexarPlantaSpt, enviarCroquiSpt, gravarRelatorioSpt, tirarCroquiSpt,
  tirarPlantaSpt, verRelatorioSpt, type RelatorioSpt as Relatorio,
} from '../../api/spt';
import { dataHora, plural } from '../../formato';
import type { ControleDoMapa, Feicao } from '../../mapaGeotecnico';
import { useSessao } from '../../sessao';
import { aoMudarTemaEfetivo, temaEfetivo } from '../../tema';
import { abrirProtegido, Aviso, BarraDeAvanco, Carregando, Dialogo, ErroDaApi, Selo } from '../../ui';

/**
 * O relatório da campanha SPT — ondas 358/359 (spec 12 §6; RN-276).
 *
 * Num lugar só: a situação e o número, o que falta para enviar, os textos
 * das seções (361: o texto automático aparece escrito na caixa; vazio no banco = automático; antes aparecia como
 * sugestão), a locação nas duas formas (D2, como no CPTu: o croqui
 * desenhado pelo sistema — fundo branco, ou a imagem capturada do mapa — ou a
 * planta em PDF anexada), a prévia em PDF e as ações do fluxo, cada uma para
 * quem tem a permissão: enviar (spt.editar), verificar e devolver
 * (spt.verificar), aprovar e emitir, e abrir a revisão (spt.aprovar).
 */
export function RelatorioSpt({ campanhaId, aoMudar }: { campanhaId: string; aoMudar(): void }) {
  const { pode } = useSessao();
  const [r, setR] = useState<Relatorio | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [rasc, setRasc] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState<{ acao: 'devolver' | 'revisao' } | null>(null);
  const [captura, setCaptura] = useState(false);
  const [progresso, setProgresso] = useState<number | null>(null);
  const planta = useRef<HTMLInputElement>(null);

  const ler = async () => { try { const x = await verRelatorioSpt(campanhaId); setR(x); setRasc({}); } catch (e) { setErro(e); } };
  useEffect(() => { void ler(); }, [campanhaId]);

  const fazer = async (nome: string, p: () => Promise<Relatorio>) => {
    if (ocupado) return;
    setOcupado(nome); setErro(null);
    try { const x = await p(); setR(x); setRasc({}); aoMudar(); } catch (e) { setErro(e); } finally { setOcupado(null); }
  };

  if (!r) return erro ? <ErroDaApi erro={erro} /> : <Carregando texto="Carregando o relatório" />;
  const ed = r.editavel && pode('spt.editar');
  const verificado = !!r.relatorio?.verificado;

  const enviarPlanta = async (arq: File) => {
    const fd = new FormData();
    fd.append('pdf', arq, arq.name);
    fd.append('meta', JSON.stringify({ titulo: arq.name.replace(/\.pdf$/i, ''), origem: 'planta do cliente' }));
    setProgresso(0);
    try { const x = await anexarPlantaSpt(campanhaId, fd, setProgresso); setR(x); } catch (e) { setErro(e); } finally { setProgresso(null); }
  };

  return (
    <section className="spt-relatorio">
      <ErroDaApi erro={erro} />
      <div className="cartao">
        <div className="cartao-cabeca">
          <h2 className="cresce">Relatório {r.numero ? <span className="mono">{r.numero}</span> : <span className="sub">(numerado no envio)</span>}</h2>
          <SeloDaCampanha situacao={r.situacao} />
          <button type="button" className="botao secundario" aria-disabled={!!ocupado}
            onClick={() => { setOcupado('previa'); abrirProtegido(`/spt/campanhas/${campanhaId}/relatorio.pdf`, 'relatorio-spt-PRELIMINAR.pdf').catch(setErro).finally(() => setOcupado(null)); }}>
            {ocupado === 'previa' ? 'Gerando…' : 'Prévia em PDF'}
          </button>
        </div>
        {r.motivo && (r.situacao === 'devolvida' || r.situacao === 'rascunho') && <Aviso tipo="atencao">Motivo: {r.motivo}</Aviso>}
        {r.relatorio && (
          <ul className="spt-assinaturas">
            <li>Elaboração: {r.relatorio.elaborado ? `${r.relatorio.elaborado.nome} · ${dataHora(r.relatorio.elaborado.em)}` : '—'}</li>
            <li>Verificação: {r.relatorio.verificado ? `${r.relatorio.verificado.nome} · ${dataHora(r.relatorio.verificado.em)}` : '—'}</li>
            <li>Aprovação: {r.relatorio.aprovado ? `${r.relatorio.aprovado.nome} · ${dataHora(r.relatorio.aprovado.em)}` : '—'}</li>
          </ul>
        )}
        {r.falta.length > 0 && (r.situacao === 'rascunho' || r.situacao === 'devolvida') && (
          <>
            <h3>Falta para enviar · {r.falta.length}</h3>
            <ul className="spt-pendencias">{r.falta.map((f, i) => <li key={i}>▲ {f.texto}</li>)}</ul>
          </>
        )}
        <div className="spt-acoes">
          {(r.situacao === 'rascunho' || r.situacao === 'devolvida') && pode('spt.editar') && (
            <button type="button" className="botao" aria-disabled={r.falta.length > 0 || !!ocupado}
              title={r.falta.length ? 'Resolva o que falta antes de enviar.' : undefined}
              onClick={() => { if (!r.falta.length) void fazer('enviar', () => acaoDoRelatorioSpt(campanhaId, 'enviar')); }}>
              {ocupado === 'enviar' ? 'Enviando…' : 'Enviar para a verificação'}
            </button>
          )}
          {r.situacao === 'em_verificacao' && pode('spt.verificar') && !verificado && (
            <button type="button" className="botao" aria-disabled={!!ocupado}
              onClick={() => void fazer('verificar', () => acaoDoRelatorioSpt(campanhaId, 'verificar'))}>Verificar</button>
          )}
          {r.situacao === 'em_verificacao' && pode('spt.verificar') && (
            <button type="button" className="botao secundario" onClick={() => setMotivo({ acao: 'devolver' })}>Devolver</button>
          )}
          {(r.situacao === 'em_verificacao' || r.situacao === 'aprovada') && pode('spt.aprovar') && (
            <button type="button" className={verificado || r.situacao === 'aprovada' ? 'botao' : 'botao secundario'}
              aria-disabled={(!verificado && r.situacao === 'em_verificacao') || !!ocupado}
              title={!verificado && r.situacao === 'em_verificacao' ? 'A verificação vem antes da aprovação.' : undefined}
              onClick={() => { if (verificado || r.situacao === 'aprovada') void fazer('emitir', () => acaoDoRelatorioSpt(campanhaId, 'emitir').then(() => verRelatorioSpt(campanhaId))); }}>
              {ocupado === 'emitir' ? 'Emitindo…' : 'Aprovar e emitir'}
            </button>
          )}
          {r.situacao === 'emitida' && pode('spt.aprovar') && (
            <button type="button" className="botao secundario" onClick={() => setMotivo({ acao: 'revisao' })}>Abrir revisão</button>
          )}
        </div>
        {r.emissoes.length > 0 && (
          <>
            <h3>Emitidos</h3>
            <ul className="spt-lista-da-os">
              {r.emissoes.map((e) => (
                <li key={e.id}>
                  <button type="button" className="botao discreto"
                    onClick={() => abrirProtegido(`/spt/relatorios/${e.id}/pdf`, `${e.numero}-R${String(e.revisao).padStart(2, '0')}.pdf`).catch(setErro)}>
                    {e.numero} · R{String(e.revisao).padStart(2, '0')}
                  </button>
                  <span className="sub"> · {dataHora(e.data_emissao)} · {e.status === 'substituido' ? 'substituído' : 'vigente'}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="cartao">
        <h3>Locação</h3>
        <div className="seg" role="group" aria-label="Forma da locação">
          <button type="button" aria-pressed={r.locacao === 'croqui'} disabled={!ed}
            onClick={() => void fazer('locacao', () => gravarRelatorioSpt(campanhaId, { locacao: 'croqui' }))}>Croqui pelo mapa</button>
          <button type="button" aria-pressed={r.locacao === 'planta'} disabled={!ed || !r.anexos.length}
            title={!r.anexos.length ? 'Anexe a planta em PDF primeiro.' : undefined}
            onClick={() => void fazer('locacao', () => gravarRelatorioSpt(campanhaId, { locacao: 'planta' }))}>Planta em PDF</button>
        </div>
        {r.locacao === 'croqui' && (
          <p className="sub">
            {r.temCroqui ? 'A prancha usa a imagem capturada do mapa, com os furos, a quadrícula, o norte e a escala desenhados por cima.'
              : 'A prancha é desenhada pelo sistema no fundo branco, com as coordenadas dos furos.'}
            {' '}{plural(r.furosComCoordenada.length, 'furo com coordenada', 'furos com coordenada')}.
          </p>
        )}
        {r.locacao === 'croqui' && !r.furosComCoordenada.length && (
          /* 372: sem coordenada, «Capturar do mapa» não tem o que desenhar — a tela diz onde lançá-la (relato do Humberto, 03/11/2026). */
          <p className="sub spt-sem-coordenada">
            Para capturar do mapa, os furos precisam de coordenada: lance a UTM na{' '}
            <Link to={`/spt/${campanhaId}/edicao`}>Edição rápida</Link> ou loque os pontos em OS › Campo › «Locar no mapa».
          </p>
        )}
        {ed && (
          <div className="spt-acoes">
            <button type="button" className="botao secundario" aria-disabled={!r.furosComCoordenada.length}
              title={!r.furosComCoordenada.length ? 'Lance as coordenadas UTM dos furos primeiro.' : undefined}
              onClick={() => { if (r.furosComCoordenada.length) setCaptura(true); }}>Capturar do mapa</button>
            {r.temCroqui && <button type="button" className="botao discreto" onClick={() => void fazer('croqui', () => tirarCroquiSpt(campanhaId))}>Voltar ao fundo branco</button>}
            <button type="button" className="botao secundario" onClick={() => planta.current?.click()}>Anexar planta em PDF</button>
            <input ref={planta} type="file" accept="application/pdf" hidden
              onChange={(e) => { const a = e.target.files?.[0]; e.target.value = ''; if (a) void enviarPlanta(a); }} />
          </div>
        )}
        {progresso !== null && <BarraDeAvanco pct={progresso * 100} rotulo="Enviando" />}
        {r.anexos.length > 0 && (
          <ul className="spt-lista-da-os">
            {r.anexos.map((a, k) => (
              <li key={a.arquivoId}>{a.titulo} <span className="sub">· PDF</span>
                {ed && <button type="button" className="botao discreto" onClick={() => void fazer('planta', () => tirarPlantaSpt(campanhaId, k))}>Tirar</button>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="cartao">
        <h3>Textos do relatório</h3>
        <p className="sub">Os textos saem automáticos, montados dos dados dos furos. Para mudar, é só escrever na caixa; «Voltar ao automático» desfaz. A tabela de compacidade e consistência entra na seção 5.</p>
        {r.secoes.map((s) => {
          /* 361: a caixa mostra o texto automático (antes era o cinza do placeholder e parecia vazia). Gravar o mesmo
             texto do automático, ou apagar tudo, volta ao automático (vazio no banco). */
          const proprio = (r.textos[s.chave] ?? '') !== '';
          const valor = rasc[s.chave] ?? (proprio ? r.textos[s.chave] : r.padrao[s.chave] ?? '');
          const gravar = (v: string) => {
            const novo = v.trim() === (r.padrao[s.chave] ?? '').trim() ? '' : v;
            if (novo !== (r.textos[s.chave] ?? '')) void fazer('textos', () => gravarRelatorioSpt(campanhaId, { textos: { [s.chave]: novo } }));
          };
          return (
            <div key={s.chave} className="spt-texto">
              <label className="spt-rotulo" htmlFor={`spt-texto-${s.chave}`}>
                <span>{s.titulo} <Selo tom={proprio ? 'atencao' : 'neutro'}>{proprio ? 'editado' : 'automático'}</Selo></span>
              </label>
              <textarea id={`spt-texto-${s.chave}`} className="campo" rows={s.chave === 'normas' ? 3 : 5} disabled={!ed}
                placeholder="Sem dados para montar o texto ainda."
                value={valor} onChange={(e) => setRasc({ ...rasc, [s.chave]: e.target.value })}
                onBlur={() => { if (rasc[s.chave] !== undefined) gravar(rasc[s.chave]); }} />
              {ed && proprio && (
                <button type="button" className="botao discreto"
                  onClick={() => { setRasc({ ...rasc, [s.chave]: undefined as unknown as string }); void fazer('textos', () => gravarRelatorioSpt(campanhaId, { textos: { [s.chave]: '' } })); }}>
                  Voltar ao automático
                </button>
              )}
            </div>
          );
        })}
      </div>

      {motivo && (
        <JanelaDoMotivo titulo={motivo.acao === 'devolver' ? 'Devolver para correção' : 'Abrir revisão do relatório'}
          rotulo={r.numero ? `Relatório ${r.numero}` : undefined}
          aoFechar={() => setMotivo(null)}
          aoConfirmar={(m) => { setMotivo(null); void fazer(motivo.acao, () => acaoDoRelatorioSpt(campanhaId, motivo.acao, { motivo: m })); }} />
      )}
      {captura && (
        <CapturaDoCroquiSpt pontos={r.furosComCoordenada} aoFechar={() => setCaptura(false)}
          aoUsar={async (fd) => { setCaptura(false); setProgresso(0); try { setR(await enviarCroquiSpt(campanhaId, fd, setProgresso)); } catch (e) { setErro(e); } finally { setProgresso(null); } }} />
      )}
    </section>
  );
}

export function JanelaDoMotivo({ titulo, rotulo, aoFechar, aoConfirmar }: { titulo: string; rotulo?: string; aoFechar(): void; aoConfirmar(m: string): void }) {
  const [m, setM] = useState('');
  return (
    <Dialogo aberto titulo={titulo} rotulo={rotulo} aoFechar={aoFechar}>
      <form onSubmit={(e) => { e.preventDefault(); if (m.trim().length >= 5) aoConfirmar(m.trim()); }}>
        <label className="spt-rotulo">Motivo (5 caracteres ou mais)
          <textarea className="campo" rows={3} autoFocus value={m} onChange={(e) => setM(e.target.value)} />
        </label>
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={m.trim().length < 5}>Confirmar</button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </form>
    </Dialogo>
  );
}

/** A captura do croqui no Mapa Geotécnico (o motor de sempre), norte para cima, sem os símbolos: a prancha desenha os furos. */
function CapturaDoCroquiSpt({ pontos, aoFechar, aoUsar }: {
  pontos: { codigo: string; latitude: number; longitude: number }[]; aoFechar(): void; aoUsar(fd: FormData): void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [capturando, setCapturando] = useState(false);

  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligar: (() => void) | null = null;
    try { if (!localStorage.getItem('labmanager.spt.croqui.fundo')) localStorage.setItem('labmanager.spt.croqui.fundo', 'satelite'); } catch { /* sem memória */ }
    void (async () => {
      const { criarMapa } = await import('../../mapaGeotecnico');
      if (!vivo || !caixa.current) return;
      const m = await criarMapa(caixa.current, {
        casa: [pontos[0].latitude, pontos[0].longitude], chaveDoFundo: 'labmanager.spt.croqui.fundo',
        chaveDasCamadas: 'labmanager.spt.croqui.camadas', tema: temaEfetivo(), tamanhoDoIcone: 30,
        dica: (f) => f.nome.replace(/[&<>"']/g, ''), textoDaLista: (f) => `Furo ${f.nome}`,
      });
      if (!vivo) { m.destruir(); return; }
      criado = m;
      m.definirRegistros(pontos.map((p): Feicao => ({
        chave: p.codigo, lugar: p.codigo, tipo: 'sp', estado: 'exec', fonte: 'ponto', estimada: false,
        nome: p.codigo, cliente: '', latitude: p.latitude, longitude: p.longitude,
      })));
      m.enquadrar();
      desligar = aoMudarTemaEfetivo((t) => m.definirTema(t));
      setControle(m);
    })().catch(() => { if (vivo) setFalhou(true); });
    return () => { vivo = false; desligar?.(); criado?.destruir(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capturar = async () => {
    if (!controle || capturando) return;
    setCapturando(true);
    try {
      const x = await controle.capturar({ tipo: 'image/png', semRegistros: true })
        ?? await controle.capturar({ tipo: 'image/png', semRegistros: true });
      if (!x) { setFalhou(true); return; }
      const fd = new FormData();
      fd.append('imagem', x.blob, 'croqui.png');
      fd.append('meta', JSON.stringify({
        origem: 'mapa', metrosPorPixel: x.metrosPorPixel, largura: x.largura, altura: x.altura,
        enquadramento: x.enquadramento, zoom: x.zoom, fundo: x.fundo, credito: x.credito,
      }));
      aoUsar(fd);
    } finally { setCapturando(false); }
  };

  return (
    <Dialogo aberto larga titulo="Capturar o croqui do mapa" rotulo="Locação dos furos" aoFechar={aoFechar}>
      <>
        <p className="sub">Enquadre os furos e capture. A prancha desenha por cima o nome de cada furo, a quadrícula, o norte e a escala gráfica.</p>
        {falhou && <p className="spt-falta">O mapa não deixou capturar a imagem (sem WebGL, ou o fundo recusou a leitura). Troque o fundo no canto do mapa, ou use a planta em PDF.</p>}
        <div ref={caixa} className="mapa-caixa spt-croqui-mapa" />
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
          <button type="button" className="botao" aria-disabled={!controle || capturando} onClick={() => void capturar()}>
            {capturando ? 'Capturando…' : 'Capturar e usar'}
          </button>
        </div>
      </>
    </Dialogo>
  );
}
